import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useCallback } from "react";

import type {
  CreateUserSkillReq,
  ListSkillsResp,
  SkillResp,
  UpdateSkillReq,
} from "../types/skills.ts";

export const skillQueries = {
  all: () => ["skills"] as const,
  list: () =>
    queryOptions({
      queryKey: [...skillQueries.all(), "list"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ListSkillsResp>("/api/a/skills", { signal });
        return response.data;
      },
    }),
  detail: (name: string | undefined) =>
    queryOptions({
      queryKey: [...skillQueries.all(), "detail", name ?? ""] as const,
      queryFn: async ({ signal }) => {
        if (!name) throw new Error("skill name required");
        const response = await api.get<SkillResp>(`/api/a/skills/${encodeURIComponent(name)}`, {
          signal,
        });
        return response.data;
      },
      enabled: !!name,
    }),
};

export const useSkills = () => {
  const { data, isLoading, error } = useQuery(skillQueries.list());
  return { skills: data ?? [], isLoading, error };
};

export const useSkill = (name: string | undefined) => {
  const { data, isLoading, error } = useQuery(skillQueries.detail(name));
  return { skill: data, isLoading, error };
};

export const useSkillMutations = () => {
  const queryClient = useQueryClient();

  // After any write, both the skills list and the slash-commands list need to
  // refetch — the dropdown's `useSlashCommands` query is a separate cache that
  // also surfaces enabled skills, and the FE would otherwise lag behind.
  const invalidateAll = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: skillQueries.all() }),
        queryClient.invalidateQueries({ queryKey: ["slash-commands"] }),
      ]),
    [queryClient],
  );

  const setEnabled = useCallback(
    async (name: string, enabled: boolean) => {
      const path = enabled ? "enable" : "disable";
      await api.post(`/api/a/skills/${encodeURIComponent(name)}/${path}`);
      await invalidateAll();
    },
    [invalidateAll],
  );

  const createUserSkill = useCallback(
    async (data: CreateUserSkillReq): Promise<SkillResp> => {
      const response = await api.post<SkillResp>("/api/a/skills", data);
      await invalidateAll();
      return response.data;
    },
    [invalidateAll],
  );

  const updateSkill = useCallback(
    async (name: string, patch: UpdateSkillReq): Promise<SkillResp> => {
      const response = await api.post<SkillResp>(
        `/api/a/skills/${encodeURIComponent(name)}`,
        patch,
      );
      await invalidateAll();
      return response.data;
    },
    [invalidateAll],
  );

  const deleteSkill = useCallback(
    async (name: string) => {
      await api.post(`/api/a/skills/${encodeURIComponent(name)}/delete`);
      // Invalidate (don't remove) so any mounted detail view refetches —
      // important for Reset on an override, where the URL still resolves
      // to the underlying system skill. For Delete on a user-kind skill,
      // the caller navigates away before the refetched 404 lands.
      await invalidateAll();
    },
    [invalidateAll],
  );

  return { setEnabled, createUserSkill, updateSkill, deleteSkill };
};
